import React from "react";

const defaultTableStyle = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: "0 10px",
  fontSize: "14px",
  color: "#2f2a36",
  background: "linear-gradient(180deg, rgba(179, 38, 170, 0.04) 0%, rgba(179, 38, 170, 0) 24%)",
  borderRadius: "14px",
  padding: "6px",
};

const defaultHeaderCellStyle = {
  background: "#7f188f",
  color: "#fff",
  fontWeight: "600",
  padding: "13px 16px",
  textAlign: "left",
  fontSize: "12px",
  letterSpacing: "0.7px",
  textTransform: "uppercase",
  // border: "1px solid rgba(255, 255, 255, 0.29)",
  boxShadow: "0 6px 14px rgba(127, 24, 143, 0.18)",
};

const defaultCellStyle = {
  padding: "14px 16px",
  verticalAlign: "middle",
  background: "#ffffff",
  borderTop: "1px solid #f0e6f4",
  borderBottom: "1px solid #f0e6f4",
};

const defaultSkeletonCellStyle = {
  boxShadow: "0 6px 14px rgba(127, 24, 143, 0.18)",
};

const getEdgeCellStyle = (index, totalColumns) => ({
  ...(index === 0
    ? {
        borderLeft: "1px solid #f0e6f4",
        borderRadius: "10px 0 0 10px"
      }
    : {}),
  ...(index === totalColumns - 1
    ? {
        borderRight: "1px solid #f0e6f4",
        borderRadius: "0 10px 10px 0" 
           }
    : {}),
});

const DataTable = ({
  columns,
  rows,
  loading = false,
  skeletonRowCount = 5,
  emptyMessage = "No data found",
  tableStyle = {},
  rowProps,
  loadingRow,
  rowKey,
}) => {
  const resolvedSkeletonRowCount =
    Number.isInteger(skeletonRowCount) && skeletonRowCount > 0
      ? skeletonRowCount
      : 5;

  return (
    <table style={{ ...defaultTableStyle, ...tableStyle }}>
      <thead>
        <tr style={{ height: "50px" }}>
          {columns.map((column, index) => {
            const isFirst = index === 0;
            const isLast = index === columns.length - 1;
            const resolvedHeaderTextAlign =
              column.headerStyle?.textAlign || column.cellStyle?.textAlign || "left";

            return (
              <th
                key={column.key || index}
                style={{
                  ...defaultHeaderCellStyle,
                  textAlign: resolvedHeaderTextAlign,
                  ...(isFirst ? { borderRadius: "8px 0 0 8px" } : {}),
                  ...(isLast ? { borderRadius: "0 8px 8px 0" } : {}),
                  ...(column.headerStyle || {}),
                }}
              >
                {column.label}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          Array.from({ length: resolvedSkeletonRowCount }).map((_, idx) => {
            if (loadingRow) {
              const customLoadingRow = loadingRow(idx, columns);
              return React.isValidElement(customLoadingRow)
                ? React.cloneElement(customLoadingRow, {
                    key: customLoadingRow.key ?? `skeleton-${idx}`,
                  })
                : customLoadingRow;
            }

            return (
              <tr key={`skeleton-${idx}`}>
                {columns.map((column, colIdx) => (
                  <td
                    key={`${column.key || colIdx}-${idx}`}
                    style={{
                      ...defaultCellStyle,
                      ...defaultSkeletonCellStyle,
                      ...getEdgeCellStyle(colIdx, columns.length),
                      textAlign: column.cellStyle?.textAlign || "left",
                    }}
                  >
                    <div className="h-4 bg-[#e7dfec] rounded-lg w-24 animate-pulse" />
                  </td>
                ))}
              </tr>
            );
          })
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} style={{ padding: "36px 16px", textAlign: "center" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "280px",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  background: "#faf5fc",
                  border: "1px dashed #ddc2e6",
                  color: "#7b5d86",
                  fontWeight: 500,
                }}
              >
                {emptyMessage}
              </div>
            </td>
          </tr>
        ) : (
          rows.map((row, rowIndex) => {
            const dynamicProps = rowProps ? rowProps(row, rowIndex) : {};
            const rowStyle = dynamicProps.style || {};
            const mergedRowProps = {
              ...dynamicProps,
              style: {
                transition: "all 0.25s ease",
                ...rowStyle,
              },
            };
            return (
              <tr key={rowKey ? rowKey(row, rowIndex) : rowIndex} {...mergedRowProps}>
                {columns.map((column, colIndex) => (
                  <td
                    key={`${column.key || colIndex}-${rowIndex}`}
                    style={{
                      ...defaultCellStyle,
                      ...getEdgeCellStyle(colIndex, columns.length),
                      ...(column.cellStyle || {}),
                    }}
                  >
                    {column.render ? column.render(row, rowIndex) : row[column.key]}
                  </td>
                ))}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};

export default DataTable;